import { ApiError, createFixture, updateFixture } from "@/lib/api/client";
import type { AmendmentRow, FixtureAmendPayload, FixtureCreatePayload } from "@/lib/api/types";

export type CreateFixtureResult =
  | { ok: true; id: string; code: string }
  | { ok: false; error: string; violations: string[] };

export type UpdateFixtureResult =
  | { ok: true; amendment: AmendmentRow }
  | { ok: false; error: string; violations: string[] };

/**
 * Create a fixture through the admin service.
 *
 * Returns rather than redirects: the generated form has a lot of state, and
 * losing it to a redirect on a validation error is the difference between
 * "fix one dropdown" and "start again".
 */
export async function createFixtureAction(
  payload: FixtureCreatePayload,
): Promise<CreateFixtureResult> {
  try {
    const created = await createFixture(payload);
    return { ok: true, id: created.id, code: created.code };
  } catch (error) {
    if (error instanceof ApiError) {
      return { ok: false, error: error.message, violations: error.violations };
    }
    return { ok: false, error: "Unexpected error creating the fixture.", violations: [] };
  }
}

/**
 * Edit a fixture through the admin service.
 *
 * Like {@link createFixtureAction}, returns rather than redirects so a
 * validation error leaves the form's state intact to fix.
 */
export async function updateFixtureAction(
  id: string,
  payload: FixtureAmendPayload,
): Promise<UpdateFixtureResult> {
  try {
    const amendment = await updateFixture(id, payload);
    return { ok: true, amendment };
  } catch (error) {
    if (error instanceof ApiError) {
      return { ok: false, error: error.message, violations: error.violations };
    }
    return { ok: false, error: "Unexpected error editing the fixture.", violations: [] };
  }
}
